import { useEffect, useMemo, useRef, useState } from "react";
import adminStyles from "../../styles/components/AdminDetailModals.module.css";
import companyStyles from "../../styles/components/CompanyDetailModal.module.css";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import { ConfirmDeleteModal } from "./ContactConfirmModals";
import { companiesService } from "../../services/companies.service";
import { useLeadContext } from "../../contexts/LeadContext";
import CountryPicker from "../../ui/inputs/CountryPicker";
import { buildCountryOptions, findCountryOption } from "../../utils/countryOptions";
import { extractApiErrorMessage } from "../../utils/apiError";
import { useOverlayClose } from "../../hooks/useOverlayClose";

const styles = { ...adminStyles, ...companyStyles };

function FieldRow({ label, children }) {
  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.fieldValue}>{children}</div>
    </div>
  );
}


export default function CompanyDetailModal({
  company,
  onClose,
  onSaved,
  onDeleted,
  onMerged,
  onOpenPerson,
  createMode = false,
  mergeMode = false,
  mergeCompanyIds = [],
  accountId = null,
}) {
  const [isEditing, setIsEditing] = useState(createMode || mergeMode);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [personsOpen, setPersonsOpen] = useState(false);
  const [personsLoading, setPersonsLoading] = useState(false);
  const [personsError, setPersonsError] = useState(null);
  const [persons, setPersons] = useState(null);
  const [personsTotal, setPersonsTotal] = useState(0);
  const personsGenerationRef = useRef(0);

  const [name, setName] = useState(company?.name ?? "");
  const [legalName, setLegalName] = useState(company?.legal_name ?? "");
  const [industry, setIndustry] = useState(company?.industry ?? "");
  const [website, setWebsite] = useState(company?.website ?? "");
  const [phone, setPhone] = useState(company?.phone ?? "");
  const [comment, setComment] = useState(company?.comment ?? "");
  const [countryId, setCountryId] = useState(company?.country ?? null);

  const { countries } = useLeadContext();
  const countryOptions = useMemo(() => buildCountryOptions(countries), [countries]);
  const companyCountry = findCountryOption(
    countryOptions,
    company?.country != null ? { id: company.country } : null,
  );

  const displayName = company?.name || (mergeMode ? "Empresa unificada" : "Nueva Empresa");

  const overlayCloseProps = useOverlayClose(onClose);

  function startEdit() {
    setName(company?.name ?? "");
    setLegalName(company?.legal_name ?? "");
    setIndustry(company?.industry ?? "");
    setWebsite(company?.website ?? "");
    setPhone(company?.phone ?? "");
    setComment(company?.comment ?? "");
    setCountryId(company?.country ?? null);
    setIsEditing(true);
  }

  function cancelEdit() {
    setSaveError(null);
    if (createMode || mergeMode) {
      onClose();
    } else {
      setIsEditing(false);
    }
  }

  useEffect(() => {
    personsGenerationRef.current += 1;
    setPersons(null);
    setPersonsTotal(0);
    setPersonsOpen(false);
    setPersonsLoading(false);
    setPersonsError(null);
  }, [company?.id]);

  async function handleTogglePersons() {
    const willOpen = !personsOpen;
    setPersonsOpen(willOpen);
    if (!willOpen || persons !== null || personsLoading) return;
    const generation = personsGenerationRef.current;
    setPersonsLoading(true);
    setPersonsError(null);
    try {
      const { results, count } = await companiesService.getPersons(company.id);
      if (generation !== personsGenerationRef.current) return;
      setPersons(results);
      setPersonsTotal(count);
    } catch {
      if (generation === personsGenerationRef.current) {
        setPersonsError("No se pudieron cargar las personas de la empresa.");
      }
    } finally {
      if (generation === personsGenerationRef.current) {
        setPersonsLoading(false);
      }
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setSaveError(null);
    try {
      await companiesService.remove(company.id);
      onDeleted?.(company.id);
      onClose();
    } catch (err) {
      console.error(err);
      setShowDelete(false);
      setSaveError(
        extractApiErrorMessage(err) || "No se pudo eliminar la empresa.",
      );
    } finally {
      setDeleting(false);
    }
  }

  async function handleSave() {
    const payload = {
      name: name.trim(),
      legal_name: legalName.trim() || null,
      industry: industry.trim() || null,
      website: website.trim() || null,
      phone: phone.trim() || null,
      comment: comment.trim() || null,
      country: countryId ?? null,
      ...(accountId != null ? { account_id: accountId } : {}),
    };
    setSaving(true);
    setSaveError(null);
    try {
      if (mergeMode) {
        const merged = await companiesService.merge(mergeCompanyIds, payload);
        onMerged?.(merged);
        return;
      }

      let result;
      if (createMode) {
        result = await companiesService.create(payload);
      } else {
        result = await companiesService.update(company.id, payload);
      }
      onSaved?.(result);
      if (!createMode) setIsEditing(false);
    } catch (err) {
      console.error(err);
      setSaveError(
        extractApiErrorMessage(err) ||
          (mergeMode
            ? "No se pudieron unificar las empresas."
            : createMode
              ? "No se pudo crear la empresa."
              : "No se pudo guardar la empresa."),
      );
    } finally {
      setSaving(false);
    }
  }


  return (
    <div className={styles.overlay} {...overlayCloseProps}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerInfo}>
            <span className={styles.modalName}>{displayName}</span>
            {company?.industry && !isEditing ? (
              <span className={styles.modalUsername}>{company.industry}</span>
            ) : null}
          </div>

          <div className={styles.headerActions}>
            {isEditing && !createMode && !mergeMode && (
              <button
                className={styles.trashBtn}
                onClick={() => setShowDelete(true)}
                aria-label="Eliminar empresa"
              >
                <TrashIcon className={styles.trashIcon} />
              </button>
            )}
          </div>
        </div>

        {mergeMode && (
          <div className={styles.mergeHint}>
            Así quedará la empresa luego de unificar {mergeCompanyIds.length}{" "}
            empresas. Revisá y ajustá los datos antes de confirmar.
          </div>
        )}

        {/* Fields */}
        <div className={styles.modalFields}>
          <FieldRow label="Nombre">
            {isEditing ? (
              <input
                className={styles.fieldInput}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Nombre de la empresa"
              />
            ) : (
              <span className={styles.fieldText}>{company?.name || "-"}</span>
            )}
          </FieldRow>

          <FieldRow label="Nombre legal">
            {isEditing ? (
              <input
                className={styles.fieldInput}
                value={legalName}
                onChange={(event) => setLegalName(event.target.value)}
                placeholder="Razón social"
              />
            ) : (
              <span className={styles.fieldText}>{company?.legal_name || "-"}</span>
            )}
          </FieldRow>

          <FieldRow label="País">
            {isEditing ? (
              <CountryPicker
                options={countryOptions}
                value={countryId != null ? { id: countryId } : null}
                onChange={(option) => setCountryId(option?.id ?? null)}
                placeholder="País"
                variant="field"
                clearable
              />
            ) : (
              <span className={styles.fieldText}>{companyCountry?.name || "-"}</span>
            )}
          </FieldRow>

          <FieldRow label="Industria">
            {isEditing ? (
              <input
                className={styles.fieldInput}
                value={industry}
                onChange={(event) => setIndustry(event.target.value)}
                placeholder="Industria"
              />
            ) : (
              <span className={styles.fieldText}>{company?.industry || "-"}</span>
            )}
          </FieldRow>

          <FieldRow label="Sitio web">
            {isEditing ? (
              <input
                className={styles.fieldInput}
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
                placeholder="https://..."
                type="url"
              />
            ) : (
              <span className={styles.fieldText}>{company?.website || "-"}</span>
            )}
          </FieldRow>

          <FieldRow label="Teléfono">
            {isEditing ? (
              <input
                className={styles.fieldInput}
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="+54 11 1234 5678"
                type="tel"
              />
            ) : (
              <span className={styles.fieldText}>{company?.phone || "-"}</span>
            )}
          </FieldRow>

          <FieldRow label="Comentario">
            {isEditing ? (
              <textarea
                className={styles.fieldTextarea}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Notas adicionales..."
                rows={3}
              />
            ) : (
              <span className={styles.fieldText}>{company?.comment || "-"}</span>
            )}
          </FieldRow>
        </div>

        {!createMode && !mergeMode && company && !isEditing && (
          <div className={styles.personsSection}>
            <button
              type="button"
              className={styles.personsToggleBtn}
              onClick={handleTogglePersons}
            >
              {personsOpen ? "Ocultar personas" : "Ver personas de la empresa"}
            </button>

            {personsOpen && (
              <>
                {personsLoading && (
                  <div className={styles.personsHint}>Cargando personas…</div>
                )}

                {!personsLoading && personsError && (
                  <div className={styles.personsError}>{personsError}</div>
                )}

                {!personsLoading && !personsError && persons && persons.length === 0 && (
                  <div className={styles.personsHint}>
                    Esta empresa no tiene personas asociadas.
                  </div>
                )}

                {!personsLoading && !personsError && persons && persons.length > 0 && (
                  <>
                    <div className={styles.personsCount}>
                      {personsTotal === 1
                        ? "Esta empresa tiene 1 persona asociada."
                        : `Esta empresa tiene ${personsTotal} personas asociadas.`}
                    </div>
                    <div className={styles.personsTableWrap}>
                      <div className={styles.personsTableHeader}>
                        <span>Persona</span>
                        <span>Cargo</span>
                      </div>
                      {persons.map((person) => {
                        const personName =
                          `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim() ||
                          person.email ||
                          "-";
                        return (
                          <button
                            type="button"
                            key={person.id}
                            className={styles.personRow}
                            onClick={() => onOpenPerson?.(person.id)}
                            title={`Abrir ${personName}`}
                          >
                            <span className={styles.personCell}>{personName}</span>
                            <span className={styles.personCell}>
                              {person.position || "-"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {personsTotal > persons.length && (
                      <div className={styles.personsHint}>
                        Se muestran las primeras {persons.length}.
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {saveError && (
          <p className={styles.formError} role="alert">
            {saveError}
          </p>
        )}

        {/* Footer */}
        <div className={styles.modalFooter}>
          {isEditing ? (
            <div className={styles.footerRow}>
              <button className={styles.cancelBtn} onClick={cancelEdit}>
                Cancelar
              </button>
              <button
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? mergeMode
                    ? "Unificando..."
                    : "Guardando..."
                  : mergeMode
                    ? "Unificar empresas"
                    : createMode
                      ? "Crear empresa"
                      : "Guardar cambios"}
              </button>
            </div>
          ) : (
            <button className={styles.editBtn} onClick={startEdit} type="button">
              <EditIcon className={styles.editIcon} />
              Editar información
            </button>
          )}
        </div>
      </div>

      {showDelete && (
        <ConfirmDeleteModal
          name={displayName}
          title="¿Eliminar empresa?"
          description={
            <>
              Esta acción no se puede deshacer. La empresa{" "}
              <strong>{displayName}</strong> será eliminada permanentemente.
            </>
          }
          onCancel={() => setShowDelete(false)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </div>
  );
}
